import { ArrayField } from '@formily/core';
import { ISchema } from '@formily/json-schema';
import {
  observer,
  ReactFC,
  RecursionField,
  useField,
  useFieldSchema,
} from '@formily/react';
import cls from 'classnames';
import React from 'react';

import { usePrefixCls } from '../../hooks/use-prefix-cls';
import { SortableContainer, SortableElement } from '../../tools/sort';
import { ArrayBase } from '../array-base';

import useStyle from './style';

/** 渲染接入纵向拖拽能力的单个数组项容器。 */
const SortableItem = SortableElement((props) => {
  const prefixCls = usePrefixCls('formily-array-items');
  const [wrapSSR, hashId] = useStyle(prefixCls);

  return wrapSSR(
    <div
      {...props}
      className={cls(`${prefixCls}-item`, hashId, props.className)}
    >
      {props.children}
    </div>,
  );
});

/** 渲染承载数组项的可排序列表容器。 */
const SortableList = SortableContainer((props) => {
  const prefixCls = usePrefixCls('formily-array-items');
  const [wrapSSR, hashId] = useStyle(prefixCls);
  return wrapSSR(
    <div
      {...props}
      className={cls(`${prefixCls}-list`, hashId, props.className)}
    >
      {props.children}
    </div>,
  );
});

/** 判断 Schema 节点是否声明数组新增入口。 */
const isAdditionComponent = (schema: ISchema) => schema['x-component']?.indexOf('Addition') > -1;

/** 从当前字段 Schema 中解析新增入口。 */
const useAddition = () => {
  const schema = useFieldSchema();
  return schema.reduceProperties((addition, schema, key) => {
    if (isAdditionComponent(schema)) {
      return <RecursionField name={key} schema={schema} />;
    }
    return addition;
  }, null);
};

interface IInternalArrayItemsProps extends React.HTMLAttributes<HTMLDivElement> {
  canMove?: (oldIndex: number, newIndex: number) => boolean;
  direction?: 'horizontal' | 'vertical';
  listStyle?: React.CSSProperties;
  sortable?: boolean;
}

/** 按当前下标解析 tuple、additionalItems 或兼容的首项回退 Schema。 */
const resolveArrayItemSchema = (schema: ISchema, index: number) => {
  if (!Array.isArray(schema.items)) return schema.items;
  const tupleItem = schema.items[index];
  if (tupleItem) return tupleItem;
  // Formily 的 ISchema 类型尚未声明 JSON Schema 合法的布尔 additionalItems，运行时需显式收窄。
  const { additionalItems } = schema as ISchema & {
    additionalItems?: boolean | ISchema;
  };
  if (additionalItems === false) return undefined;
  if (additionalItems === true) {
    const unconstrainedSchema: ISchema = {};
    return unconstrainedSchema;
  }
  if (
    additionalItems
    && typeof additionalItems === 'object'
  ) {
    return additionalItems;
  }
  // 未声明 additionalItems 时保留历史首项复用行为，避免普通旧 Schema 的额外项突然空白。
  return schema.items[0];
};

/** 渲染数组字段的可排序项、Schema 新增入口和宿主扩展操作。 */
const InternalArrayItems: ReactFC<IInternalArrayItemsProps> = observer((props) => {
  const {
    canMove,
    children,
    direction = 'vertical',
    listStyle,
    sortable = true,
    ...containerProps
  } = props;
  const field = useField<ArrayField>();
  const prefixCls = usePrefixCls('formily-array-items');
  const [wrapSSR, hashId] = useStyle(prefixCls);
  const schema = useFieldSchema();
  const addition = useAddition();
  const dataSource = Array.isArray(field.value) ? field.value : [];
  if (!schema) throw new Error('can not found schema object');
  return wrapSSR(
    <ArrayBase>
      <div
        {...containerProps}
        className={cls(prefixCls, hashId, containerProps.className)}
        onChange={() => { }}
      >
        <SortableList
          className={`${prefixCls}-sort-helper`}
          direction={direction}
          list={dataSource.slice()}
          style={listStyle}
          onSortEnd={(event) => {
            // 只读字段或宿主显式禁用排序时必须阻断最终写操作，避免拖拽污染 Store 数据。
            if (!sortable || field.pattern !== 'editable') return;
            const { oldIndex, newIndex } = event;
            if (canMove && !canMove(oldIndex, newIndex)) return;
            field.move(oldIndex, newIndex);
          }}
        >
          {dataSource?.map((_item, index) => {
            const items = resolveArrayItemSchema(schema, index);
            return (
              <ArrayBase.Item
                key={index}
                index={index}
                record={() => field.value?.[index]}
              >
                <SortableItem
                  key={`item-${index}`}
                  index={index}
                  lockAxis={direction === 'horizontal' ? 'x' : 'y'}
                >
                  <div className={`${prefixCls}-item-inner`}>
                    {items ? (
                      <RecursionField name={index} schema={items} />
                    ) : null}
                  </div>
                </SortableItem>
              </ArrayBase.Item>
            );
          })}
        </SortableList>
        {addition}
        {children}
      </div>
    </ArrayBase>,
  );
});

/** 渲染数组项的卡片或分隔线外观容器。 */
const Item: ReactFC<
  React.HTMLAttributes<HTMLDivElement> & {
    type?: 'card' | 'divide'
  }
> = (props) => {
  const prefixCls = usePrefixCls('formily-array-items');
  const [wrapSSR, hashId] = useStyle(prefixCls);
  return wrapSSR(
    <div
      {...props}
      className={cls(
        `${prefixCls}-${props.type || 'card'}`,
        hashId,
        props.className,
      )}
      onChange={() => { }}
    >
      {props.children}
    </div>,
  );
};

export const ArrayItems = Object.assign(ArrayBase.mixin(InternalArrayItems), {
  Item,
});

ArrayItems.displayName = 'ArrayItems';

export default ArrayItems;
